import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth.store';
import { refresh } from './endpoints/auth';

/**
 * Every route except /auth/login, /auth/refresh and /auth/logout requires
 * `Authorization: Bearer <accessToken>` (see backend router.go). The access
 * token lives only in memory (the Zustand auth store) — never in
 * localStorage/sessionStorage — and is re-derived via silent refresh on
 * reload. `withCredentials` is required so the browser actually sends/
 * receives the httpOnly `fishoil_refresh_token` cookie the refresh flow
 * depends on.
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  // Without this, a backend that's down but not actively refusing the
  // connection (e.g. blocked by a firewall instead of "connection refused")
  // makes the request hang until the OS's own TCP timeout — tens of
  // seconds, sometimes longer. App.tsx blocks the entire app behind the
  // boot-time silent refresh, so a hang here reads as "the app is stuck",
  // not just one failed request. Fail predictably instead.
  timeout: 8000,
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// A concurrent burst of 401s (several queries firing at once after the
// access token expires) — or two callers silently refreshing on boot at
// once (React StrictMode's double-invoked effect in dev; two tabs loading
// at once in prod) — must trigger exactly one /auth/refresh call, not one
// per caller: the backend rotates the refresh token on every use, so a
// second concurrent call reusing the now-revoked cookie would 401 and log
// the user straight back out. Every caller (this interceptor, and
// auth.store's initSession) shares this one in-flight promise instead.
let refreshInFlight: Promise<string> | null = null;

export async function refreshAccessToken(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const session = await refresh();
        useAuthStore.getState().setSession(session);
        return session.accessToken;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');

    if (status !== 401 || !original || original._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      const accessToken = await refreshAccessToken();
      original.headers = original.headers ?? {};
      original.headers.Authorization = `Bearer ${accessToken}`;
      return apiClient(original);
    } catch (refreshError) {
      useAuthStore.getState().clearSession();
      return Promise.reject(refreshError);
    }
  },
);

/** Best-effort extraction of the backend's `{ "error": "..." }` envelope for display in the UI. */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}
